import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Input, Spinner } from '@material-tailwind/react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { ArrowLeftIcon, PlusIcon, PencilIcon, HistoryIcon, ArrowUpCircleIcon, ArrowDownCircleIcon, PackageXIcon, XIcon } from 'lucide-react';
import moment from 'moment';
import { URLS } from '../../router-x/routes';
import { usePermissions } from '../../custom-hooks/use-permissions';
import { useGetInventoryItemById, useGetInventoryTransactions, useGetInventoryItemHistory, useGetInventoryConsumptionTrend } from '../../custom-hooks/use-inventory';
import { CATEGORY_META, TRANSACTION_TYPE_META, formatQty } from './utils';
import AddTransactionModal from './components/AddTransactionModal';
import ItemFormModal from './components/ItemFormModal';
import PhysicalCountCard from './components/PhysicalCountCard';

const TYPE_ICON = { in: ArrowUpCircleIcon, out: ArrowDownCircleIcon, breakage: PackageXIcon };

const tooltipStyle = { borderRadius: 12, border: '1px solid #E6DDCF', fontSize: 12, boxShadow: '0 12px 24px -12px rgba(40,22,8,0.25)' };

const InventoryItemDetail = () => {
    const { itemId } = useParams();
    const { can } = usePermissions();
    const [date, setDate] = useState('');
    const [logOpen, setLogOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    const { data, isLoading } = useGetInventoryItemById(itemId);
    const { data: txData, isLoading: txLoading } = useGetInventoryTransactions(itemId, { date: date || undefined });
    const { data: history = [] } = useGetInventoryItemHistory(showHistory ? itemId : null);
    const { data: trend = [] } = useGetInventoryConsumptionTrend({ itemId, days: 14 });

    if (isLoading) {
        return <div className="flex min-h-[50vh] items-center justify-center"><Spinner className="h-8 w-8 text-inv-brand" /></div>;
    }

    const item = data?.item;
    if (!item) {
        return <div className="py-16 text-center text-[#8C8175]">Item not found.</div>;
    }

    const meta = CATEGORY_META[item.category] || CATEGORY_META.others;
    const isLow = item.reorderLevel > 0 && item.currentRemaining <= item.reorderLevel;
    const transactions = txData?.transactions || [];

    return (
        <div className="min-h-screen bg-inv-cream px-4 py-6 md:px-8 md:py-8">
            <Link to={`${URLS.ADMIN.INVENTORY}?tab=items`} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#6B675F] hover:text-inv-brand">
                <ArrowLeftIcon size={15} /> Back to Inventory
            </Link>

            <div className="mb-5 flex flex-col justify-between gap-4 rounded-2xl border border-inv-cream-200 bg-white p-5 md:flex-row md:items-center">
                <div>
                    <div className="flex items-center gap-2">
                        <h1 className="text-xl font-extrabold text-inv-ink">{item.name}</h1>
                        {isLow && <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-600">Low Stock</span>}
                    </div>
                    <span className="mt-1 inline-flex items-center gap-1.5 text-xs text-[#8C8175]">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.hex }} />
                        {meta.label} · Reorder at {formatQty(item.reorderLevel, item.unitOfMeasure)}
                        {item.dailyMaxLimit ? ` · Daily max ${item.dailyMaxLimit}` : ''}
                    </span>
                </div>
                <div className="flex w-full items-center justify-between gap-3 md:w-auto">
                    <div className="text-left md:text-right">
                        <div className="text-3xl font-extrabold leading-none text-inv-ink">{item.currentRemaining}</div>
                        <div className="text-xs text-[#8C8175]">{item.unitOfMeasure ? `${item.unitOfMeasure} ` : ''}remaining</div>
                    </div>
                    <div className="flex items-center gap-3">
                        {can('FX31', 'edit_items') && (
                            <button onClick={() => setEditOpen(true)} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-inv-cream-200 text-[#6B675F] hover:bg-inv-cream-100">
                                <PencilIcon size={16} />
                            </button>
                        )}
                        {can('FX31', 'log') && (
                            <button onClick={() => setLogOpen(true)} className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl bg-inv-brand px-4 py-2.5 text-sm font-semibold text-white hover:bg-inv-brand-dark">
                                <PlusIcon size={16} /> Log Entry
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {trend.length > 0 && can('FX31-1', 'view_trend') && (
                <div className="mb-5 rounded-2xl border border-inv-cream-200 bg-white p-5">
                    <h3 className="mb-3 font-bold text-inv-ink">14-Day Consumption</h3>
                    <div className="h-[160px] w-full">
                        <ResponsiveContainer width="100%" height="100%" debounce={200}>
                            <AreaChart data={trend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                                <CartesianGrid vertical={false} stroke="#e1e0d9" />
                                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} tickFormatter={(d) => moment(d).format('D MMM')} />
                                <YAxis tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip contentStyle={tooltipStyle} labelFormatter={(d) => moment(d).format('DD MMM YYYY')} />
                                <Area type="monotone" dataKey="out" name={TRANSACTION_TYPE_META.out.label} stroke={TRANSACTION_TYPE_META.out.hex} fill={TRANSACTION_TYPE_META.out.hex} fillOpacity={0.12} strokeWidth={2} dot={{ r: 3 }} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            <div className="rounded-2xl border border-inv-cream-200 bg-white p-5">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <h3 className="font-bold text-inv-ink">Daily Log</h3>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="w-full shrink-0 sm:w-56">
                            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} label="Jump to date" />
                        </div>
                        {date && (
                            <button onClick={() => setDate('')} className="flex h-9 w-9 items-center justify-center rounded-lg text-[#8C8175] hover:bg-inv-cream-100">
                                <XIcon size={14} />
                            </button>
                        )}
                        {can('FX31-1', 'audit_history') && (
                            <button onClick={() => setShowHistory((v) => !v)} className="flex items-center gap-1.5 rounded-lg border border-inv-cream-200 px-3 py-2 text-xs font-semibold text-[#6B675F] hover:bg-inv-cream-100">
                                <HistoryIcon size={13} /> {showHistory ? 'Hide' : 'Audit'} History
                            </button>
                        )}
                    </div>
                </div>

                {txLoading ? (
                    <div className="flex justify-center py-8"><Spinner className="h-5 w-5 text-inv-brand" /></div>
                ) : transactions.length === 0 ? (
                    <p className="py-8 text-center text-sm text-[#8C8175]">No entries {date ? 'on this date' : 'yet'}.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[760px] text-left text-sm">
                            <thead>
                                <tr className="border-b border-inv-cream-200 text-xs font-semibold uppercase tracking-wide text-[#8C8175]">
                                    <th className="px-3 py-2.5">Date & Time</th>
                                    <th className="px-3 py-2.5">Type</th>
                                    <th className="px-3 py-2.5">Who</th>
                                    <th className="px-3 py-2.5 text-right">Qty</th>
                                    <th className="px-3 py-2.5 text-right">Remaining</th>
                                    <th className="px-3 py-2.5">Location</th>
                                    <th className="px-3 py-2.5">Note</th>
                                    <th className="px-3 py-2.5">Logged By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map((t) => {
                                    const Icon = TYPE_ICON[t.type];
                                    const typeMeta = TRANSACTION_TYPE_META[t.type];
                                    return (
                                        <tr key={t._id} className="border-b border-inv-cream-100 last:border-0">
                                            <td className="px-3 py-2.5 text-[#6B675F]">{moment(t.occurredAt).format('DD MMM YYYY, h:mm A')}</td>
                                            <td className="px-3 py-2.5">
                                                <span className="inline-flex items-center gap-1.5 font-medium" style={{ color: typeMeta.hex }}>
                                                    <Icon size={14} /> {typeMeta.label}
                                                </span>
                                                {t.exceedsDailyMax && <span className="ml-1.5 rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-600">MAX</span>}
                                            </td>
                                            <td className="px-3 py-2.5 text-inv-ink">{t.consumer?.name || '—'}</td>
                                            <td className="px-3 py-2.5 text-right font-semibold text-inv-ink">{t.quantity}</td>
                                            <td className="px-3 py-2.5 text-right text-[#6B675F]">{t.remainingAfter}</td>
                                            <td className="px-3 py-2.5 text-[#8C8175]">{t.floor ? `Floor ${t.floor.number}${t.building?.name ? ` — ${t.building.name}` : ''}` : '—'}</td>
                                            <td className="max-w-[220px] whitespace-normal break-words px-3 py-2.5 text-[#8C8175]">{t.note || '—'}</td>
                                            <td className="px-3 py-2.5 text-[#8C8175]">{t.createdBy?.name || t.createdBy?.email || '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {showHistory && (
                    <div className="mt-5 border-t border-inv-cream-200 pt-4">
                        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8C8175]">Audit History</h4>
                        {history.length === 0 ? (
                            <p className="text-sm text-[#8C8175]">No changes recorded yet.</p>
                        ) : (
                            <ul className="space-y-1.5 text-sm">
                                {history.map((h, i) => (
                                    <li key={i} className="flex items-center justify-between rounded-lg bg-inv-cream-100 px-3 py-2">
                                        <div>
                                            <div className="text-inv-ink">{h.comment}</div>
                                            <div className="text-xs text-[#8C8175]">by {h.changedBy?.name || 'Unknown'}</div>
                                        </div>
                                        <span className="text-xs text-[#8C8175]">{moment(h.changedAt).format('DD MMM YYYY, h:mm A')}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            <div className="mt-5">
                <PhysicalCountCard item={item} />
            </div>

            <AddTransactionModal isOpen={logOpen} onClose={() => setLogOpen(false)} item={item} />
            <ItemFormModal isOpen={editOpen} onClose={() => setEditOpen(false)} item={item} />
        </div>
    );
};

export default InventoryItemDetail;
